import React, { useEffect, useRef } from 'react';
import { TextField } from '@material-ui/core';
import {
  Fab, CircularProgress, Grid, MenuItem, Select, InputLabel, FormControl,
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import {
  Formik, Form, FormikProps, Field, FormikHelpers
} from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import clsx from 'clsx';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';

import WrapperSimple from '../../layout-components/ExampleWrapperSimple';
import UseStyles from '../../components/styles/loadingButton';
import CalculationMethod, { CalculationMethodList } from '../../models/enums/calculationMethod';
import categoryDriver, { CategoryDriverList } from '../../models/enums/categoryDriver';
import tariffService from '../../services/tariffService';
import { FixedValue } from '../../models/interfaces/tariff';
import Dictionary from '../../models/interfaces/dictionary';
import DynamicRows from './components/dynamicRows';
import CustomerMultiSelect, {
  ALL_CUSTOMERS_OPTION, companiesToIds, countSpecificCompanies, formatSyncMessage,
  hasMultipleSpecificCompanies
} from './components/customerMultiSelect';
import customerService from '../../services/customerService';
import { getErrorMessage } from '../../config/firebase';
import { useAuth } from '../../contexts/auth';
import AutocompleteCustomField from '../../components/autocompleteCustomField';
import useDebounce from '../../hooks/useDebounce';
import { CurrencyFormatCustom } from '../../components/mask';

interface FormValues {
  categoryDriver: categoryDriver;
  description: string;
  calculationMethod: CalculationMethod;
  valuePerAdditionalStop: number | undefined;
  valueFlag: number | undefined;
  valueByKm: number | undefined;
  valueByStop: number | undefined;
  valueMinimum: number | undefined;
  valueDisplacement: number | undefined;
  company: Dictionary;
  companyName: string | null;
  companies: Dictionary[];
}

interface ParamTypes {
  subsidiaryId: string;
}

export default function page() {
  const { user } = useAuth();
  const history = useHistory();
  const { subsidiaryId } = useParams<ParamTypes>();
  const { enqueueSnackbar } = useSnackbar();
  const [success, setSuccess] = React.useState(false);
  const classes = UseStyles();
  const [customers, setCustomers] = React.useState<Dictionary[]>();
  const [fixedValues, setFixedValues] = React.useState<FixedValue[]>([
    {
      id: uuidv4(),
      distance: 1,
      value: undefined,
      valueBack: undefined
    }
  ]);
  const [inputCustomerValue, setInputCustomerValue] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingCompanyCount, setPendingCompanyCount] = React.useState(0);
  const pendingSubmitRef = useRef<{ values: FormValues; actions: FormikHelpers<FormValues> } | null>(null);
  const customCalculationMethodList = CalculationMethodList.filter((x) => x.key === 3 || x.key === 4);

  const buttonClassname = clsx({ [classes.buttonSuccess]: success });
  const claims = user?.claims;
  const isAdmin = Boolean(claims?.admin);

  const handleCustomerInputChange = (event: any, newValue: string) => {
    setInputCustomerValue(newValue);
  };

  const debouncedFetchCustomer = useDebounce((str: string) => {
    customerService.getListForAutocomplete(subsidiaryId, str).then((res) => {
      if (!res?.hits?.length) return;
      const list = res?.hits.map((x: any) => ({ key: x.objectID, value: x.name ?? x.businessName }));
      list.unshift({ key: null, value: 'Todos' });
      setCustomers(list);
    });
  });

  const executeSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
    const payload = {
      categoryDriver: values.categoryDriver,
      description: values.description,
      calculationMethod: values.calculationMethod,
      valuePerAdditionalStop: values.valuePerAdditionalStop!,
      valueFlag: values.valueFlag!,
      valueByKm: values.valueByKm!,
      valueByStop: values.valueByStop!,
      valueMinimum: values.valueMinimum!,
      disabledAt: null,
      fixedValues,
      companyName: values.companyName,
      valueDisplacement: values.valueDisplacement ?? 0
    };

    try {
      if (isAdmin) {
        await tariffService.createAsync(payload, subsidiaryId, values.company?.key);
        setSuccess(true);
        history.push(`/subsidiaries/${subsidiaryId}`);
      } else {
        const result = await tariffService.syncForCompanies(
          payload,
          subsidiaryId,
          companiesToIds(values.companies)
        );
        setSuccess(true);
        enqueueSnackbar(formatSyncMessage(result), {
          variant: result.failed.length ? 'warning' : 'success'
        });
        history.push('/settings/tariffs');
      }
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), { variant: 'error' });
      setSuccess(false);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleConfirmSubmit = async () => {
    const pending = pendingSubmitRef.current;
    if (!pending) return;

    setConfirmOpen(false);
    pending.actions.setSubmitting(true);
    await executeSubmit(pending.values, pending.actions);
    pendingSubmitRef.current = null;
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    pendingSubmitRef.current = null;
  };

  return (
    <WrapperSimple sectionHeading="Nova tarifa">
      <Formik
        initialValues={{
          categoryDriver: categoryDriver.Undefined,
          description: '',
          calculationMethod: CalculationMethod.Undefined,
          valuePerAdditionalStop: undefined,
          valueFlag: undefined,
          valueByKm: undefined,
          valueByStop: undefined,
          valueMinimum: undefined,
          valueDisplacement: undefined,
          company: { key: '', value: 'Todos' },
          companyName: null,
          companies: [ALL_CUSTOMERS_OPTION]
        }}
        validationSchema={Yup.object().shape({
          categoryDriver: Yup.number().required().positive(),
          description: Yup.string().required(),
          calculationMethod: Yup.number().required().positive(),
          ...(isAdmin ? {} : {
            companies: Yup.array().min(1, 'Selecione ao menos um cliente.')
          })
        })}
        onSubmit={async (values: FormValues, actions) => {
          if (!isAdmin && hasMultipleSpecificCompanies(values.companies)) {
            pendingSubmitRef.current = { values, actions };
            setPendingCompanyCount(countSpecificCompanies(values.companies));
            actions.setSubmitting(false);
            setConfirmOpen(true);
            return;
          }

          await executeSubmit(values, actions);
        }}
      >
        {(props: FormikProps<FormValues>) => {
          const {
            errors, touched, isSubmitting, values, handleBlur, setFieldValue
          } = props;

          useEffect(() => {
            if (isAdmin) {
              debouncedFetchCustomer(inputCustomerValue);
            }
          }, [inputCustomerValue, isAdmin]);

          return (
            <Form noValidate autoComplete="off">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                  {isAdmin ? (
                    customers?.length && (
                      <AutocompleteCustomField
                        name="company"
                        options={customers}
                        onInputChange={handleCustomerInputChange}
                        noOptionsText="Informe um cliente"
                        onBlur={handleBlur}
                        errors={errors.company}
                        touched={touched.company}
                        label="Clientes"
                        value={values.company}
                      />
                    )
                  ) : (
                    <CustomerMultiSelect
                      subsidiaryId={subsidiaryId}
                      value={values.companies}
                      onChange={(companies) => setFieldValue('companies', companies)}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Field
                      as={Select}
                      required
                      label="Categoria"
                      name="categoryDriver"
                      error={Boolean(errors.categoryDriver && touched.categoryDriver)}
                    >
                      <MenuItem key={0} value={0}>Selecione...</MenuItem>
                      {CategoryDriverList.map((item: Dictionary) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    required
                    variant="outlined"
                    label="Descrição"
                    name="description"
                    error={Boolean(errors.description && touched.description)}
                    helperText={errors.description && touched.description && errors.description}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Método de cálculo</InputLabel>
                    <Field
                      as={Select}
                      required
                      label="Método de cálculo"
                      name="calculationMethod"
                      error={Boolean(errors.calculationMethod && touched.calculationMethod)}
                    >
                      <MenuItem key={0} value={0}>Selecione...</MenuItem>
                      {customCalculationMethodList.map((item: any) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={2}>
                  <Field
                    as={TextField}
                    fullWidth
                    required
                    variant="outlined"
                    label="Taxa Deslocamento/Km"
                    name="valueDisplacement"
                    InputProps={{
                      inputComponent: CurrencyFormatCustom as any
                    }}
                    error={Boolean(errors.valueDisplacement && touched.valueDisplacement)}
                    helperText={errors.valueDisplacement && touched.valueDisplacement && errors.valueDisplacement}
                  />
                </Grid>
                <DynamicRows fixedValues={fixedValues} setFixedValues={setFixedValues} />
                <Grid item xs={12}>
                  <Grid container justifyContent="flex-end" spacing={3}>
                    <div className={classes.wrapper}>
                      <Fab
                        aria-label="save"
                        color="primary"
                        className={buttonClassname}
                        type="submit"
                      >
                        {success ? <CheckIcon /> : <SaveIcon />}
                      </Fab>
                      {isSubmitting && <CircularProgress size={68} className={classes.fabProgress} />}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        aria-labelledby="tariff-multi-company-dialog-title"
      >
        <DialogTitle id="tariff-multi-company-dialog-title">
          Confirmação
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você selecionou
            {' '}
            {pendingCompanyCount}
            {' '}
            empresas. Será criada uma tabela de tarifas para cada uma delas com os mesmos valores informados. Deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmSubmit} color="primary" variant="contained" autoFocus>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </WrapperSimple>
  );
}
